import { useState } from 'react'
import { api, fmt } from '../lib/api.js'
import { Chip, Empty, Icon } from '../components/ui.jsx'

const TYPE_LABEL = {
  commitment_clock: 'COMMITMENT CLOCK',
  evidence_threshold: 'EVIDENCE THRESHOLD',
  people_change: 'PEOPLE CHANGE',
  market_theme: 'MARKET THEME',
  coverage_gap: 'COVERAGE GAP',
}

// Where research meets outreach. Four types fire outreach; coverage_gap fires
// research instead, which keeps the research engine inside the loop.
export default function Triggers({ triggers, attention, onOpen, onCampaign, onReload }) {
  const [busy, setBusy] = useState(null)

  const dismiss = async (id) => {
    setBusy(id)
    await api.dismissTrigger(id)
    await onReload()
    setBusy(null)
  }

  const outreach = triggers.filter((t) => t.action !== 'research')
  const needsDecision = triggers.filter((t) => t.needs_confirmation).length

  return (
    <>
      <div className="topbar">
        <h1>Triggers</h1>
        <span className="topbar-note">
          {triggers.length} fired · {needsDecision} need a decision
        </span>
        <div className="topbar-right">
          <span className="card-sub">a trigger is evidence plus a date</span>
        </div>
      </div>

      <div className="body split">
        <div className="col">
          <div className="card">
            <div className="card-header">
              <span className="card-title">Fired</span>
              <span className="card-sub">{outreach.length} outreach · {triggers.length - outreach.length} research</span>
            </div>
            <div>
              {triggers.length === 0 ? <Empty>Nothing has fired. Everything is either actioned or dismissed.</Empty> : null}
              {triggers.map((t) => (
                <div key={t.id} className={`trg ${t.type}`}>
                  <div className="trg-head">
                    <Chip
                      kind={
                        t.type === 'commitment_clock'
                          ? 'awaiting'
                          : t.type === 'evidence_threshold'
                          ? 'replied'
                          : t.type === 'people_change'
                          ? 'sequenced'
                          : t.type === 'market_theme'
                          ? 'named'
                          : 'plain'
                      }
                    >
                      {TYPE_LABEL[t.type] || t.type}
                    </Chip>
                    <span className="card-sub">{t.standing ? 'STANDING' : `FIRED ${fmt.date(t.fired_at).toUpperCase()}`}</span>
                    {t.days_remaining != null ? (
                      <span className="card-sub" style={{ marginLeft: 'auto', color: 'var(--warn)', fontWeight: 600 }}>
                        {t.days_remaining < 0 ? `${Math.abs(t.days_remaining)} DAYS OVERDUE` : `${t.days_remaining} DAYS TO DEADLINE`}
                      </span>
                    ) : t.accounts?.length ? (
                      <span className="card-sub" style={{ marginLeft: 'auto' }}>
                        {t.accounts.length} ACCOUNT{t.accounts.length === 1 ? '' : 'S'}
                      </span>
                    ) : null}
                  </div>

                  <div className="trg-title">{t.headline}</div>
                  <div className="trg-detail">{t.detail}</div>

                  {t.needs_confirmation ? (
                    <div className="card-sub" style={{ marginTop: 5, color: 'var(--warn)' }}>
                      Needs checking: {t.confirm_note}
                    </div>
                  ) : null}

                  <div className="trg-foot">
                    {(t.accounts || []).slice(0, 4).map((a) => (
                      <button key={a.slug} className="chip acct" onClick={() => onOpen(a.slug)} style={{ border: 'none' }}>
                        {a.name.toUpperCase()}
                        {a.figure ? ` ${a.figure}` : ''}
                      </button>
                    ))}
                    {(t.accounts || []).length > 4 ? <span className="card-sub">+{t.accounts.length - 4}</span> : null}
                    {t.counts ? (
                      <>
                        <Chip kind="plain">{t.counts.to_top_up} TO TOP UP</Chip>
                        <Chip kind="plain">{t.counts.not_researched} NOT RESEARCHED</Chip>
                      </>
                    ) : null}
                    {t.source ? <span className="card-sub">{t.source}</span> : null}

                    <span style={{ marginLeft: 'auto' }} />
                    {t.action === 'campaign' ? (
                      <button className="btn ghost" onClick={() => onCampaign(t)}>Build campaign</button>
                    ) : t.action === 'draft' ? (
                      <button className="btn ghost" onClick={() => onOpen(t.accounts[0]?.slug)}>Open and draft</button>
                    ) : null}
                    {t.standing ? null : (
                      <button className="btn" onClick={() => dismiss(t.id)} disabled={busy === t.id}>
                        Dismiss
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rail" style={{ padding: 14 }}>
          {/* Pipedrive's activity-based selling: you control activities, not outcomes. */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">No next step</span>
              <Chip kind={attention.no_next_step.length ? 'stale' : 'plain'}>{attention.no_next_step.length}</Chip>
            </div>
            <div className="card-body">
              <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--ink-soft)', marginBottom: 9 }}>
                In a campaign with nothing scheduled. You cannot control whether they reply, only whether something is booked.
              </div>
              {attention.no_next_step.length === 0 ? (
                <Empty>Everything in a campaign has a next step.</Empty>
              ) : (
                attention.no_next_step.map((a) => (
                  <div className="row" key={a.slug} style={{ alignItems: 'baseline', padding: '3px 0', borderBottom: '0.5px solid var(--line-soft)' }}>
                    <span style={{ fontSize: 'var(--fs-pill)', fontWeight: 500 }}>{a.name}</span>
                    <span className="card-sub">{a.why}</span>
                    <button className="btn link" style={{ marginLeft: 'auto' }} onClick={() => onOpen(a.slug)}>Set</button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Pipedrive's rotting: idle time made visible, colour-coded. */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Going stale</span>
              <span className="card-sub">idle over 14 days</span>
            </div>
            <div className="card-body">
              {attention.stale.length === 0 ? (
                <Empty>Nothing has gone stale.</Empty>
              ) : (
                attention.stale.map((a) => (
                  <div className="row" key={a.slug} style={{ padding: '6px 0', borderBottom: '0.5px solid var(--line-soft)' }}>
                    <div style={{ width: 3, height: 26, background: a.days_idle > 21 ? 'var(--neg)' : 'var(--warn)', flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--fs-pill)', fontWeight: 500 }}>{a.name}</div>
                      <div className="kpi-sub" style={{ color: a.days_idle > 21 ? 'var(--neg)' : 'var(--warn)' }}>
                        {a.days_idle} days since {fmt.date(a.last_touch)}
                      </div>
                    </div>
                    <button className="btn link" style={{ marginLeft: 'auto' }} onClick={() => onOpen(a.slug)}>Chase</button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="card" style={{ background: 'var(--ai-tint)', borderColor: 'var(--ai-border)' }}>
            <div className="card-header" style={{ borderColor: 'var(--ai-border)' }}>
              <span className="card-title" style={{ color: 'var(--ai-ink)' }}>
                <Icon name="spark" width="13" height="13" style={{ verticalAlign: -2, marginRight: 5 }} />
                No re-scan yet
              </span>
            </div>
            <div className="card-body notice-body">
              Every trigger here is computed from the research already on disk. Nothing compares two points in time, so a
              number that moved since 27 August will not fire until a re-scan exists to diff against.
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
